import { Box, HStack, Image, Text, VStack } from '@chakra-ui/react';
import { useNavigate } from '@tanstack/react-router';
import CustomButton from '@/components/atoms/CustomButton';
import { formatDomainLabel } from '@/constants/contentDomains';
import useContentAuthorProfile from '@/hooks/content/useContentAuthorProfile';
import { getContentTypeLabel } from '@/modules/content/common/contentLabels';
import {
  getCreatorName,
  getCreatorOrganization,
  getCreatorProfileId,
  getPrimaryRole,
} from '@/modules/content/common/creatorDisplay';
import { formatReaderDate } from '@/modules/content/feed/reader/model/formatReaderDate';
import { TContent, TContentDomain } from '@/types/Content.types';

const AuthorHeader = ({ content }: { content: TContent }) => {
  const navigate = useNavigate();
  const isPlatform = content.isPlatformContent;
  const creator = content.creator;

  // Attribution comes from `ContentDto.creator`, which ships with the content itself, so
  // the header renders complete on first paint. The author-profile request is now only a
  // progressive enhancement for `headline` — the one field the creator block lacks — and
  // never gates name, role, organisation, avatar or content count. Platform content has
  // no creator profile at all, so it is not fetched there.
  const { profile } = useContentAuthorProfile(content.authorUserId, !isPlatform);

  const authorName = getCreatorName(creator, isPlatform, profile?.creatorDisplayName?.trim() || profile?.name);

  // `roles` is ordered by precedence, so the first entry is the one to show. Organisation
  // is skipped for platform content, where it is also "MyOmnia" and would read as
  // "MyOmnia · MyOmnia".
  const attribution = [getPrimaryRole(creator), isPlatform ? null : getCreatorOrganization(creator)]
    .filter(Boolean)
    .join(' · ');

  const subtitle =
    attribution ||
    profile?.headline ||
    (content.domains?.length ? content.domains.map((d) => formatDomainLabel(d as TContentDomain)).join(' · ') : 'Content Creator');

  const avatarUrl = creator?.photoUrl || profile?.photoUrl || '/svg/generic-user.svg';
  const published = formatReaderDate(content.publishedAt ?? content.createdAt);
  const contentCreated = creator?.publishedContentCount ?? profile?.contentCreated;

  // Prefers `creator.id`, falling back to `authorUserId`; null for platform content.
  const creatorProfileId = getCreatorProfileId(content);

  return (
    <HStack justify="space-between" align="center" gap="16px" flexWrap="wrap">
      <HStack gap="14px" align="center">
        <Box width="56px" height="56px" borderRadius="full" overflow="hidden" backgroundColor="background.lightPurple" flexShrink={0}>
          <Image src={avatarUrl} alt={authorName} width="100%" height="100%" objectFit="cover" />
        </Box>
        <VStack align="flex-start" gap="2px">
          <Text fontSize="18px" fontWeight={800} color="text.darkBlue">
            {authorName}
          </Text>
          <Text fontSize="13px" fontStyle="italic" fontFamily="Lora" color="text.mediumBlue">
            {subtitle}
          </Text>
          <HStack gap="14px" marginTop="2px" color="text.mediumGray" flexWrap="wrap">
            <Text fontSize="12px">{getContentTypeLabel(content.type)}</Text>
            {published && <Text fontSize="12px">Published {published}</Text>}
            {!isPlatform && contentCreated !== undefined && (
              <Text fontSize="12px">
                <Text as="span" fontWeight={700} color="text.mediumBlue">
                  Content Created:
                </Text>{' '}
                {contentCreated}
              </Text>
            )}
          </HStack>
        </VStack>
      </HStack>

      {creatorProfileId && (
        <CustomButton
          label="View Profile"
          isTransparent
          borderColor="primary.500"
          labelColor="primary.500"
          labelHoverColor="extra.white"
          hoverBackgroundColor="primary.500"
          width="140px"
          onClick={() => navigate({ to: '/explore/creator/$userId', params: { userId: creatorProfileId } })}
        />
      )}
    </HStack>
  );
};

export default AuthorHeader;
